import classNames from 'classnames'

import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import type { CtaProps } from '~/components/atoms/Ctas/Ctas'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { TeamMemberCardProps } from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import TeamMemberCard from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './TeamModule.module.css'

export interface TeamModuleProps {
  title: string
  introText?: string
  teamMembers?: TeamMemberCardProps[]
  theme?: ComponentTheme
  ctas: CtaProps[]
}

export default function TeamModule({
  title,
  introText,
  teamMembers,
  theme = ComponentTheme.primary,
  ctas,
}: TeamModuleProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)

  const renderDesktopView = () => (
    <div className={styles.teamList}>
      {teamMembers &&
        teamMembers.map((item: TeamMemberCardProps, i: number) => (
          <div className={styles.listItem} key={`team-member-item-${i}`}>
            <TeamMemberCard
              useVariant
              showSummary
              showEmail={false}
              showViewProfile
              {...item}
            />
          </div>
        ))}
    </div>
  )

  const renderMobileView = () => {
    if (!teamMembers) return null
    return (
      <DynamicSlider
        slidesPerView={1.15}
        slidesPerViewMed={1}
        slidesPerViewLrg={3}
        slidesPerViewHuge={4}
        overflowVisible={true}
        title={''}
        spaceBetween={1}
        hideArrowsMobile
        wrapperClass={styles.sliderWrapperClass}
      >
        {teamMembers.map((member: TeamMemberCardProps, i: number) => (
          <div
            className={styles.mobileListItem}
            key={`mobile-team-member-${i}`}
          >
            <TeamMemberCard {...member} />
          </div>
        ))}
      </DynamicSlider>
    )
  }

  return (
    <Fixture theme={theme}>
      <Container>
        <div className={classNames(styles.base)}>
          <div className={styles.headingContainer}>
            <div className={styles.headingTitleContainer}>
              <Text size={TextSizes.primaryTitleLrg} className={styles.title}>
                {title}
              </Text>
              <Button
                className={styles.viewTeamButton}
                variant={ButtonVariants.textLink}
                link="/our-people"
              >
                Meet the team
              </Button>
            </div>
            <Text size={TextSizes.bodyMed} className={styles.introText}>
              {introText}
            </Text>
          </div>
          <div className={classNames(styles.contentContainer)}>
            {isMobile ? renderMobileView() : renderDesktopView()}
          </div>
        </div>
      </Container>
    </Fixture>
  )
}
